import { forwardRef, useState } from 'react'
import { Plus, Minus } from 'lucide-react'
import { cn } from '../../utils/helpers'

export interface NumberInputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type' | 'onChange'> {
  label?: string
  error?: string
  value: number
  onChange: (value: number) => void
  min?: number
  max?: number
  step?: number
  showControls?: boolean
}

export const NumberInput = forwardRef<HTMLInputElement, NumberInputProps>(
  ({ 
    className, 
    label, 
    error, 
    value, 
    onChange, 
    min = 0, 
    max, 
    step = 1,
    showControls = true,
    disabled,
    ...props 
  }, ref) => {
    const [isFocused, setIsFocused] = useState(false)

    const handleIncrement = () => {
      const newValue = value + step
      if (max === undefined || newValue <= max) {
        onChange(newValue)
      }
    }

    const handleDecrement = () => {
      const newValue = value - step
      if (newValue >= min) {
        onChange(newValue)
      }
    }

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = parseFloat(e.target.value)
      if (!isNaN(newValue)) {
        if ((max === undefined || newValue <= max) && newValue >= min) {
          onChange(newValue)
        }
      }
    }

    const canDecrement = value > min && !disabled
    const canIncrement = (max === undefined || value < max) && !disabled

    return (
      <div className="w-full">
        {label && (
          <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
            {label}
          </label>
        )}
        <div className={cn(
          'flex items-stretch gap-2',
          showControls && 'flex-row'
        )}>
          {showControls && (
            <button
              type="button"
              onClick={handleDecrement}
              disabled={!canDecrement}
              className={cn(
                'flex items-center justify-center min-w-[48px] min-h-[48px] rounded-lg border-2',
                'bg-white dark:bg-gray-800 transition-all duration-200',
                'active:scale-95',
                canDecrement 
                  ? 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 hover:border-primary' 
                  : 'border-gray-200 dark:border-gray-700 text-gray-400 dark:text-gray-600 cursor-not-allowed'
              )}
            >
              <Minus className="h-5 w-5" />
            </button>
          )}
          
          <input
            ref={ref}
            type="number"
            value={value}
            onChange={handleInputChange}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
            min={min}
            max={max}
            step={step}
            disabled={disabled}
            className={cn(
              'flex min-h-[48px] w-full rounded-lg border-2 bg-white dark:bg-gray-800 px-4 py-3 text-center text-xl font-semibold ring-offset-background',
              'text-gray-900 dark:text-gray-100',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2',
              'disabled:cursor-not-allowed disabled:opacity-50',
              'transition-all duration-200',
              '[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none',
              isFocused ? 'border-primary' : 'border-gray-300 dark:border-gray-600',
              error && 'border-red-500 focus-visible:ring-red-500',
              showControls ? 'flex-1' : 'w-full',
              className
            )}
            {...props}
          />
          
          {showControls && (
            <button
              type="button"
              onClick={handleIncrement}
              disabled={!canIncrement}
              className={cn(
                'flex items-center justify-center min-w-[48px] min-h-[48px] rounded-lg border-2',
                'bg-white dark:bg-gray-800 transition-all duration-200',
                'active:scale-95',
                canIncrement 
                  ? 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 hover:border-primary' 
                  : 'border-gray-200 dark:border-gray-700 text-gray-400 dark:text-gray-600 cursor-not-allowed'
              )}
            >
              <Plus className="h-5 w-5" />
            </button>
          )}
        </div>
        {error && <p className="text-sm text-red-500 mt-1.5">{error}</p>}
      </div>
    )
  }
)

NumberInput.displayName = 'NumberInput'
